import React, {useContext} from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import get from 'lodash/get';

import {classNames} from '~/src/utilities/helpers/css';
import {ButtonFormat} from '~/src/utilities/constants/formats';
import {Feature} from '~/src/utilities/constants/feature';
import {isEnabled} from '~/src/utilities/helpers/feature';
import {CommentButtonLoader} from '~/src/components/comment/button/CommentButton';
import {BookmarksButtonLoader} from '~/src/components/bookmarksButton/BookmarksButton';
import {ShareButtonLoader} from '~/src/components/share/button/ShareButton';
import {InteractionSources} from '~/src/utilities/constants/tracking';
import {SettingsContext} from '~/src/components/context/SettingsContext';

import css from './ActivityBar.module.scss';

const CommentButton = dynamic({
    loader: () => import('~/src/components/comment/button/CommentButton'),
    loading: () => <CommentButtonLoader />,
    ssr: false
});

const BookmarksButton = dynamic({
    loader: () => import('~/src/components/bookmarksButton/BookmarksButton'),
    loading: () => <BookmarksButtonLoader />,
    ssr: false
});

const ShareButton = dynamic({
    loader: () => import('~/src/components/share/button/ShareButton'),
    loading: () => <ShareButtonLoader />,
    ssr: false
});

const ActivityBar = ({data, metadataId, settings, openCommentPage = false}) => {
    const bookmarkCount = Number(get(data, 'bookmarks')) || 0;
    const sharesCount = Number(get(data, 'shares')) || 0;
    const commentCount = Number(get(data, 'comments')) || 0;
    const {siteArea} = useContext(SettingsContext);

    const classes = classNames(css, 'activityBar', siteArea);

    return (
        <div className={classes}>
            <div className={css.activityBarItem}>
                {isEnabled(Feature.COMMENTS) && (
                    <CommentButton
                        metadataId={metadataId}
                        format={ButtonFormat.BUTTON_ACTION_INLINE}
                        activityCount={commentCount}
                        isDisabled={!get(settings, 'commentingEnabled')}
                        openCommentPage={openCommentPage}
                        source={InteractionSources.QUICKACTION_ARTICLE}
                    />
                )}

                {isEnabled(Feature.BOOKMARKS) && (
                    <BookmarksButton
                        metadataId={metadataId}
                        format={ButtonFormat.BUTTON_ACTION_INLINE}
                        activityCount={bookmarkCount}
                        source={InteractionSources.QUICKACTION_ARTICLE}
                    />
                )}

                {isEnabled(Feature.SHARE) && (
                    <ShareButton
                        metadataId={metadataId}
                        format={ButtonFormat.BUTTON_ACTION_INLINE}
                        activityCount={sharesCount}
                        isDisabled={!get(settings, 'sharingEnabled')}
                        source={InteractionSources.QUICKACTION_ARTICLE}
                    />
                )}
            </div>
        </div>
    );
};

if (process.env.NODE_ENV === 'development') {
    ActivityBar.propTypes = {
        metadataId: PropTypes.string.isRequired,
        settings: PropTypes.shape({
            bookmarks: PropTypes.string,
            shares: PropTypes.string
        }),
        data: PropTypes.shape({
            commentingEnabled: PropTypes.bool,
            sharingEnabled: PropTypes.bool
        }),
        openCommentPage: PropTypes.bool
    };
}

export default ActivityBar;
